import React, { useState, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import Loading from "./Loading";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import { useStream } from 'react-fetch-streams';
import PGLogo from "../assets/images/pg-horizontal-logo.png";
import AdServer from "../assets/images/ad-server.png";
import HeaderBidding from "../assets/images/header-bidding.png";
import PolicyViolations from "../assets/images/policy-violations.png";
import GoodIcon from "../assets/images/good-icon.png";
import BadIcon from "../assets/images/bad-icon.png";
import Signup from "./Signup";
import HeroSection from "./HeroSection";
import { CATCHY_PHRASES, FULLVIEW_DESC_MAP, SIGN_UP_MESSAGES, SUMMARY_MESSAGES_MAP } from '../messages.js';
import {logToCloudWatch, logForSentry} from '../utils';
import config from '../config';
import ErrorInfo from './ErrorInfo';
import ReactGA from "react-ga4";


function getMessageByPercentage(percentage, messages){
  switch (true) {
    case (percentage < 25):
      return messages[0];
    case (percentage < 60):
      return messages[1];
    case (percentage < 80):
      return messages[2];
    default:
      return messages[3];
  }
}

const getStreamingAPIUrlFormatted = (webLink) => {
  let url;
  if (!webLink)
    return "";
  if(webLink.includes("://")){
    url = new URL(webLink)
  }
  else{
    url = new URL(`http://${webLink}`)
  }
  return url.hostname.replace("www.", "");
}

let streamStarted = false;
let startTime;
let signUpMessage = SIGN_UP_MESSAGES[3]

export default function Result({email, website}) {
  const [streamLoading, setStreamLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [notificationLoading, setNotificationsLoading] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [streamingData, setStreamingData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [notificationData, setNotificationData] = useState(null);
  const [streamingApiUrl, setStreamingApiUrl] = useState(`${config.streaming_url}?domain=${getStreamingAPIUrlFormatted(website)}`);
  const [error, setError] = useState(null);

  const loading = streamLoading || summaryLoading || notificationLoading;

  const fetchSummaryData = useCallback(async () => {
    try {
      setSummaryLoading(true);
      let response = await fetch(`${config.summary_data_url}?domain=${getStreamingAPIUrlFormatted(website)}&public_grader=true`)
      if (response.status === 200 || response.status === 204){
        response = await response.json();
        setSummaryData(response);
        setSummaryLoading(false);
      } else {
        setError({ status: response.status, message: "Unable to fetch summary data" })
        setStreamingApiUrl("")
        setSummaryLoading(false);
        setSummaryData(null);
        logForSentry(
          "Unable to fetch summary data",
          {status: response.status},
          {
            domain: website,
            email: email,
            response
          }
        )
      }
    } catch(e) {
      setSummaryLoading(false);
      setSummaryData(null);
      setError({ message: "Error in fetching summary data" })
      setStreamingApiUrl("")
      logForSentry(
        "Error in fetching summary data",
        {
          domain: website,
          email: email,
          error: e
        }
      )
    }
  }, [email, website]);

  const fetchNotificationData = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const url = config.detailed_data_url.includes('testMode=true')
          ? `${config.detailed_data_url}&domain=${getStreamingAPIUrlFormatted(website)}` : `${config.detailed_data_url}?domain=${getStreamingAPIUrlFormatted(website)}`;
      let response = await fetch(url);
      if (response.status === 200 || response.status === 204){
        response = await response.json();
        setNotificationData(response.data);
        setNotificationsLoading(false);
      } else {
        setError({ status: response.status, message: "Unable to fetch detailed data" })
        setStreamingApiUrl("")
        setNotificationData(null);
        setNotificationsLoading(false);
        logForSentry(
          "Unable to fetch detailed data",
          {status: response.status},
          {
            domain: website,
            email: email,
            response
          }
        )
      }
    } catch(e) {
      setNotificationData(null);
      setNotificationsLoading(false);
      setError({ message: "Error in fetching notification data" })
      setStreamingApiUrl("")
      logForSentry(
        "Error in fetching notification data",
        {
          domain: website,
          email: email,
          error: e
        }
      )
    }
  }, [email, website]);

  const onNext = useCallback(async res => {
    if (!streamStarted) {
      startTime = new Date();
      streamStarted = true;
    }
    try {
      const data = await res.json();
      if( data.completed === 'True' ) {
        logToCloudWatch(startTime, new Date(), website);
        fetchSummaryData();
        fetchNotificationData();
        setStreamLoading(false);
        ReactGA.event('navigate', {
          category: 'Engagement',
          label: 'Web Grader - Result page shown to user',
        });
      } else if (data.message) { // when streaming api returns error it sends a message field.
        setStreamLoading(false);
        setError({ status: 400, message: "Unable to fetch streaming data" })
        setStreamingApiUrl("")
        logForSentry(
          "Unable to fetch streaming data",
          {status: 400},
          {
            domain: website,
            email: email,
            response: data
          }
        )
      }
      setStreamingData(data);
    } catch(e) {
      setStreamLoading(false);
      setStreamingData(null)
      setError({ status: res.staus, message: "Error in fetching streaming data" })
      setStreamingApiUrl("")
      logForSentry(
        "Error in fetching streaming data",
        {status: res.staus},
        {
          domain: website,
          email: email,
          response: res,
          error: e
        }
      )
    }
  }, [fetchSummaryData, website, email, fetchNotificationData]);

  useStream(streamingApiUrl, {onNext});

  const onSignupShow = (e) => {
    const btnId = e.target.id;
    if (btnId.includes('gam-ad-setup')) {
      signUpMessage = SIGN_UP_MESSAGES[0];
    } else if (btnId.includes('header-bidding')) {
      signUpMessage = SIGN_UP_MESSAGES[1];
    } else if (btnId.includes('policy-violations')) {
      signUpMessage = SIGN_UP_MESSAGES[2];
    } else {
      signUpMessage = SIGN_UP_MESSAGES[3];
    }
    ReactGA.event('click_custom', {
      category: 'Conversion',
      label: 'Web Grader - A CTA button was clicked and the sign up form was shown'
    });
    setShowSignup(true);
  };

  const onSignupClose = () => {
    ReactGA.event('click_custom', {
      category: 'Engagement',
      label: 'Web Grader - Sign up form closed by user'
    });
    setShowSignup(false);
  };

  const refreshPage = () => {
    ReactGA.event('click_custom', {
      category: 'Conversion',
      label: 'Web Grader - User clicked on "Retry" button after seeing the error message.'
    });
    setError(null);
    setStreamingApiUrl(`${config.streaming_url}?domain=${getStreamingAPIUrlFormatted(website)}`)
    setStreamLoading(true);
  }

  const categoryPercentages = useMemo(() => {
    return (!(summaryData?.group_data)) ? {} : summaryData.group_data.reduce((acc, curr) => {
      return {...acc, [curr.name]: curr.percentage};
    }, {});
  }, [summaryData]);

  if (!loading && error) {
    ReactGA.event('navigate', {
      category: 'Engagement',
      label: 'Web Grader - Error page shown to user',
    });
    return <ErrorInfo message={{ title: "Something went terribly wrong!", subtitle: "It looks like something went wrong, click the button below to try again." }} reload={refreshPage} />
  }

  return loading ? <Loading progress={streamingData ? Number(streamingData.completed_percentage) : 0} /> : <><Grid container component="main" sx={{
    backgroundColor: '#fafafa00',
    minHeight: '100vh'
  }}>
    <Grid 
    item xs={0} md={3} component={Paper} elevation={0} 
    sx={{
      backgroundColor: '#F6F7FC',
      display: { xs: 'none', md: 'block'}
    }}>
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        maxWidth: '25%',
        overflowY: 'auto',
        maxHeight: '100vh'
      }}>
        <Box sx={{
          textAlign: 'center',
          mt: 2
        }}>
          <img id="pg-result-logo" src={PGLogo} alt="PubGuru Logo"  loading="lazy" />
        </Box>
        <AnalysisContainer website={website} summaryData={summaryData} onSignupShow={onSignupShow} />
      </Box>
    </Grid>
    <Grid item xs={12} md={9} component={Paper} elevation={0}
    >
      <Box sx={{
          textAlign: 'center',
          mt: 2,
          display: { xs: 'block', md: 'none'}
        }}>
          <img id="pg-result-logo" src={PGLogo} alt="PubGuru Logo"  loading="lazy" />
        </Box>
      <HeroSection percentage={summaryData && summaryData.total_pass_score} onClick={onSignupShow} />
      <Grid
        sx={{
          display: { xs: 'block', md: 'none'}
        }}
      >
        <AnalysisContainer website={website} summaryData={summaryData} onSignupShow={onSignupShow} />
      </Grid>
      {notificationData && notificationData.map((item, i) => <FullItemView key={i} onClick={onSignupShow} {...item} percentage={categoryPercentages[item.title]} />)}
    </Grid>
  </Grid>
  <Signup showSignup={showSignup} onSignupClose={onSignupClose} initialEmail={email} initialWebsite={website} signUpMessage={signUpMessage}/>
  </>
}


const getColor = (value) => {
  if (value < 60) return 'error';
  if (value < 80) return 'warning';
  return 'success';
}

function AnalysisContainer({website, summaryData, onSignupShow}){
  const scores = summaryData.group_data.map(item => item.percentage);
  const names  = summaryData.group_data.map(item => item.name);
  const catchyPhase = CATCHY_PHRASES[names[scores.indexOf(Math.min(...scores))]];
  return (
    <Card sx={{ mx: 2, my: 2 }}>
    <CardContent>
      <Typography sx={{ fontSize: 20, fontWeight: 600, textAlign: 'center' }}>
        Analysis for {website}
      </Typography>
      <CircularProgressWithLabel value={summaryData?.total_pass_score} />
      {summaryData?.group_data?.map((item, i) => {
          return <SummaryItemView key={i} title={item.name} subtitle={item.description} percentage={item.percentage} />;
        })}
      <Typography sx={{ fontSize: 12, textAlign: 'center' }}>
        {catchyPhase}
      </Typography>
      <Button
        id='btn-summary-cta'
        variant="contained"
        fullWidth
        size="large"
        onClick={onSignupShow}
        sx={{
          my: 2,
          px: 2,
          py: 1,
          backgroundColor: '#23B6E4',
          color: '#FFFFFF',
          fontWeight: 600,
          fontSize: 15,
          '&:hover': {
            backgroundColor: '#22a3cb',
          },
        }}
      >
        unlock full version for free
      </Button>
      <Typography sx={{ fontSize: 12, textAlign: 'center'}}>
        No credit card required
      </Typography>
    </CardContent>
  </Card>
  )
}

function SummaryItemView({title, percentage}){
  return <Box sx={{ my: 3 }}>
    <Grid container spacing={3}>
      <Grid item xs>
        <Typography sx={{fontSize: 16, fontWeight: 600}}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs="auto">
        <Typography sx={{fontSize: 16, fontWeight: 600}}>
          {`${Math.round(percentage)}%`}
        </Typography>
      </Grid>
    </Grid>
    <Typography sx={{color: 'rgba(0, 0, 0, 0.6)', fontSize: 14, mb: 1}}>
      {getMessageByPercentage(percentage, SUMMARY_MESSAGES_MAP[title])}
    </Typography>
    <LinearProgress variant="determinate" value={percentage} color={getColor(percentage)} />
  </Box>
}

const CATEGORY_IMAGE_MAP = {
  "Policy Violations": PolicyViolations,
  "Header Bidding": HeaderBidding,
  "GAM Ad Setup": AdServer
}

function FullItemView({title, percentage, issues, onClick}){
  const all_good = issues.slice(0, 3).every(iss => iss.good);
  const id = title.replaceAll(' ', '-').toLowerCase();
  return <Box sx={{ my: 2, p: 4 }}>
    <Grid className="category-title" container spacing={3}>
      <Grid className="image-grid" item xs="12" md="auto">
        <img className="issue-icon" src={CATEGORY_IMAGE_MAP[title]} alt={title} />
      </Grid>
      <Grid item xs="12" md>
        <Box sx={{ display: 'flex',}}>
          <Typography sx={{fontSize: 20, fontWeight: 600, mb: 1}}>
            {title}
          </Typography>
          <Chip sx={{ mx: 2 }} label={`${Math.round(percentage)}%`} color={getColor(percentage)} />
        </Box>
        <Typography sx={{color: '#4A4C4D', fontSize: 16, mb: 1}}>
          {getMessageByPercentage(percentage, FULLVIEW_DESC_MAP[title])}
        </Typography>
      </Grid>
    </Grid>
    <Grid container spacing={3} sx={{ my: 2}}>
      {issues.slice(0, 3).map((issue, i) => <Grid key={i} item xs={12} md={4}><IssueCard id={id + '-' + i} onClick={onClick} {...issue} /></Grid>)}
    </Grid>
    <Button
      id={`btn-${id}-cta`}
      variant="outlined"
      size="large"
      onClick={onClick}
      disabled={all_good}
      sx={{
        my: 2,
        px: 2,
        py: 1,
        color: '#22a3cb',
        borderColor: '#22a3cb',
        fontWeight: 600,
        fontSize: 15,
        '&:hover': {
          backgroundColor: '#22a3cb',
          color: '#FFFFFF',
          borderColor: '#22a3cb',
        },
      }}
    >
      {all_good ? 'NO ISSUES WITH HEADER BIDDING SETUP' : `VIEW ISSUES WITH ${title}` }
    </Button>
  </Box>
}

function IssueCard({title, content, good, onClick, id}){
  return <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} elevation={2}>
    <CardContent>
      <Box sx={{mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <img src={good ? GoodIcon : BadIcon} alt={good ? 'Good' : 'Bad'} />
        <Typography sx={{fontSize: 16, fontWeight: 600, ml: 2 }}>
          {title}
        </Typography>
      </Box>
      <Typography>
        {content}
      </Typography>
      <Chip sx={{ position: 'absolute', top: 16,  right: 0, borderRadius: '8px 0 0 8px', fontSize: 13, fontWeight: 700 }} label={good ? 'GOOD' : 'POOR'} color={good ? 'success' : 'error'} />
    </CardContent>
    <CardActions>
      <Button id={`btn-${id}-cta`} disabled={good} onClick={onClick} sx={{
        color: '#22a3cb',
        fontSize: 13,
        fontWeight: 700,
        '&:hover': {
          backgroundColor: '#e5f3f7',
        },
      }}>{good ? 'No Action Required' : 'View All Issues'}</Button>
    </CardActions>
  </Card>
}

function CircularProgressWithLabel({value}) {

  return (
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', my: 3, textAlign: 'center' }}>
      <CircularProgress variant="determinate" value={value} color={getColor(value)} size={150} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontWeight: 'bold',
            fontSize: 34,
            color: 'rgba(0, 0, 0, 0.6)'
          }}
        >{`${Math.round(value)}%`}</Typography>
      </Box>
    </Box>
  );
}

Result.propTypes = {
  email: PropTypes.string.isRequired,
  website: PropTypes.string.isRequired,
};

AnalysisContainer.propTypes = {
  website: PropTypes.string.isRequired,
  onSignupShow: PropTypes.func.isRequired,
  summaryData: PropTypes.object,
};

SummaryItemView.propTypes = {
  title: PropTypes.string.isRequired,
  percentage: PropTypes.number.isRequired,
};

FullItemView.propTypes = {
  title: PropTypes.string.isRequired,
  percentage: PropTypes.number.isRequired,
  issues: PropTypes.array.isRequired,
  onClick: PropTypes.func.isRequired,
};

IssueCard.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  good: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

CircularProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};